import {
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { useEffect, useState } from 'react'
import moment from 'moment'
import { getTransactionLabel } from './facility-history-columns'
import { FacilityHistoryEntry } from './facility-history-types'

interface Props {
  entry: FacilityHistoryEntry | null
  opened: boolean
  onClose: () => void
  onSubmit: (entry: FacilityHistoryEntry, reason: string) => Promise<void> | void
}

const MIN_REASON_LENGTH = 5

export default function FacilityHistoryReverseModal({
  entry,
  opened,
  onClose,
  onSubmit,
}: Props) {
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (opened) setReason('')
  }, [opened, entry?.id])

  if (!entry) {
    return null
  }

  const label = getTransactionLabel(entry)
  const processed = moment(entry.processedAt)
  const subtitle = processed.isValid()
    ? `${label} • ${processed.format('DD-MMM-YYYY hh:mm A')}`
    : label
  const changesCount =
    entry.modifiedFields.length +
    (entry.modifiedLoans ?? []).reduce(
      (sum, l) => sum + l.modifiedFields.length,
      0,
    )

  const trimmedReason = reason.trim()
  const canSubmit =
    !isSubmitting && trimmedReason.length >= MIN_REASON_LENGTH

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      await onSubmit(entry, trimmedReason)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton
      size={700}
      centered
      padding="xl"
      styles={{
        title: { width: '100%' },
        body: { paddingTop: 8 },
      }}
      title={
        <Stack spacing={4}>
          <Text fw={700} fz={28} c="#DC2626">
            Reverse Transaction
          </Text>
          <Text fz="lg" c="dimmed">
            {subtitle}
          </Text>
        </Stack>
      }
    >
      <Stack spacing="lg">
        {changesCount > 0 ? (
          <Box
            sx={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: 6,
              padding: '16px 20px',
            }}
          >
            <Text fz="lg" c="#7F1D1D">
              This action will undo all {changesCount} field{' '}
              {changesCount === 1 ? 'change' : 'changes'} made in this transaction.
            </Text>
          </Box>
        ) : null}

        <Textarea
          required
          minRows={6}
          autosize
          label="Reason for Reversal"
          placeholder="Please provide a reason for reversing this transaction..."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          description={`A detailed comment (at least ${MIN_REASON_LENGTH} characters) is required to reverse this transaction`}
          styles={{
            label: { fontSize: '1.1rem', fontWeight: 600, marginBottom: 6 },
            description: { fontSize: '0.95rem' },
            input: { fontSize: '1rem', lineHeight: 1.5 },
          }}
        />

        <Group position="right" spacing="md" mt="sm">
          <Button
            variant="default"
            size="md"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            size="md"
            disabled={!canSubmit}
            loading={isSubmitting}
            leftIcon={<IonIcon name="arrow-undo-outline" />}
            onClick={handleSubmit}
          >
            Submit Reversal
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
